import { z } from 'zod';

const US_ZIP_REGEX = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_REGEX = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const addressSchema = z
  .object({
    streetAddress: z.string().min(1, 'Street address is required'),
    city: z.string().min(1, 'City is required'),
    state: z.string().min(1, 'State is required'),
    zip: z.string().min(1, 'ZIP code is required'),
    country: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.country === 'US' && data.zip && !US_ZIP_REGEX.test(data.zip)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Invalid ZIP code (e.g. 12345 or 12345-6789)',
        path: ['zip'],
      });
    }
    if (data.country === 'CA' && data.zip && !CA_POSTAL_REGEX.test(data.zip)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid postal code (e.g. A1B 2C3)', path: ['zip'] });
    }
  });

const addressBaseFields = z.object({
  streetAddress: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string().optional(),
});

export const accountContactFormSchema = z
  .object({
    firstName: z.string().min(1, 'First name is required'),
    lastName: z.string().min(1, 'Last name is required'),
    title: z.string().optional(),
    email: z.string().email('Invalid email (e.g. example@mail.com)').or(z.literal('')).optional(),
    phone: z
      .string()
      .min(1, 'Phone is required')
      .regex(/^\(\d{3}\) \d{3}-\d{4}$/, 'Invalid phone format (e.g. (555) 123-4567)'),
    contactAddress: addressSchema,
    sameAsContact: z.boolean(),
    deliveryAddress: addressBaseFields,
  })
  .superRefine((data, ctx) => {
    if (!data.sameAsContact) {
      if (!data.deliveryAddress.streetAddress?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Street address is required',
          path: ['deliveryAddress', 'streetAddress'],
        });
      }
      if (!data.deliveryAddress.city?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'City is required', path: ['deliveryAddress', 'city'] });
      }
      if (!data.deliveryAddress.state?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'State is required', path: ['deliveryAddress', 'state'] });
      }
      if (!data.deliveryAddress.zip?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ZIP code is required',
          path: ['deliveryAddress', 'zip'],
        });
      } else {
        const country = data.deliveryAddress.country;
        if (country === 'US' && !US_ZIP_REGEX.test(data.deliveryAddress.zip)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid ZIP code (e.g. 12345 or 12345-6789)',
            path: ['deliveryAddress', 'zip'],
          });
        }
        if (country === 'CA' && !CA_POSTAL_REGEX.test(data.deliveryAddress.zip)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Invalid postal code (e.g. A1B 2C3)',
            path: ['deliveryAddress', 'zip'],
          });
        }
      }
    }
  });

export type AccountContactFormValues = z.infer<typeof accountContactFormSchema>;

export const accountContactFormDefaultValues: AccountContactFormValues = {
  title: '',
  email: '',
  phone: '',
  lastName: '',
  firstName: '',

  contactAddress: {
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  },

  sameAsContact: true,

  deliveryAddress: {
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  },
};
